<template>
	<div class="dx-swatch-additional side-navigation-menu" @click="forwardClick">
		<slot />
		<div v-if="reorderMode" class="reorder-mode-banner">
			<div class="reorder-mode-text">
				<i class="dx-icon-dragvertical"></i>
				<span>Reorder Mode</span>
			</div>
			<div class="reorder-mode-actions">
				<DxButton icon="save" text="Save" type="success" styling-mode="contained" @click="saveReorder" :disabled="reorderBusy" />
				<DxButton icon="close" text="Cancel" type="normal" styling-mode="text" @click="cancelReorder" :disabled="reorderBusy" />
			</div>
		</div>
		<div class="menu-container">
			<DxSortable v-if="reorderMode" :allow-reordering="true" filter=".dx-treeview-item" @drag-change="menuDragChange" @drag-end="menuDragEnd">
				<dx-tree-view ref="treeViewRef" :data-source="items" key-expr="path" selection-mode="single" @item-context-menu="treeViewContextMenu"
					:focus-state-enabled="false" expand-event="click" @item-click="handleItemClick" width="100%" />
			</DxSortable>
			<dx-tree-view v-else ref="treeViewRef" :data-source="items" key-expr="path" selection-mode="single" @item-context-menu="treeViewContextMenu"
				:focus-state-enabled="false" expand-event="click" @item-click="handleItemClick" width="100%" />
			<DxContextMenu ref="contextMenuRef" v-model:data-source="contextMenuItems" target=".menu-container .dx-treeview-item" @item-click="contextMenuItemClick" />
		</div>
	</div>
</template>

<script>
import DxTreeView from 'devextreme-vue/tree-view';
import { DxContextMenu } from 'devextreme-vue/context-menu';
import { DxSortable } from 'devextreme-vue/sortable';
import { DxButton } from 'devextreme-vue/button';
import { sizes } from '../utils/media-query';
import navigation from '../app-navigation';
import { inject, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import GroupCache from '../groups';
import Page from '../pages';
import notify from 'devextreme/ui/notify';

export default {
	props: {
		compactMode: Boolean
	},
	setup(props, context) {
		const route = useRoute();
		const router = useRouter();
		const $http = inject('$http');

		const isLargeScreen = sizes()['screen-large'];
		const items = navigation.map((item) => {
			if (item.path && !(/^\//.test(item.path))) {
				item.path = `/${item.path}`;
			}
			return { ...item, expanded: isLargeScreen }
		});

		const treeViewRef = ref(null);
		const contextMenuRef = ref(null);
		const reorderMode = ref(false);
		const reorderBusy = ref(false);
		const originalItemsSnapshot = ref(null);		// Snapshot of items before reordering began
		const contextMenuItems = ref([
			{ id: 'edit', icon: 'edit', text: 'Edit', visible: true },
			{ id: 'delete', icon: 'remove', text: 'Delete', visible: true },
			{ id: 'reorder', icon: 'dragvertical', text: 'Reorder', visible: true },
		]);
		const contextItemActive = ref(null);

		function forwardClick(...args) {
			context.emit("click", args);
		}

		function handleItemClick(e) {
			if (!e.itemData.path || props.compactMode) {
				return;
			}
			router.push(e.itemData.path);

			const pointerEvent = e.event;
			pointerEvent.stopPropagation();
		}

		function updateSelection() {
			if (!treeViewRef.value || !treeViewRef.value.instance) {
				return;
			}

			// Try to select the exact path first
			let pathToSelect = route.path;
			let selectedNode = treeViewRef.value.instance.selectItem(pathToSelect);
			
			// If no match found, try progressively shorter paths (to handle /group/page/123 -> /group/page)
			// This handles cases with context where a mote ID is appended to the page path
			if (!selectedNode) {
				const pathSegments = route.path.split('/').filter(s => s);
				while (pathSegments.length > 1 && !selectedNode) {
					pathSegments.pop();
					pathToSelect = '/' + pathSegments.join('/');
					selectedNode = treeViewRef.value.instance.selectItem(pathToSelect);
				}
			}
			
			treeViewRef.value.instance.expandItem(pathToSelect);
		}

		onMounted(() => {
			updateSelection();
			if (props.compactMode) {
				treeViewRef.value.instance.collapseAll();
			}
		});

		watch(() => route.path,
			() => { updateSelection(); }
		);

		watch(() => props.compactMode,
			() => {
				if (props.compactMode) {
					treeViewRef.value.instance.collapseAll();
				} else {
					updateSelection();
				}
			}
		);

		// Wait for our group cache to be ready, then replace the placeholder in app-navigation with our list and refresh.
		// TODO: Seems to have a bug on initial sign-in where we get 401 errors and menu doesn't load!
		GroupCache.waitForData().then(() => {
			console.log("SideNavMenu: GroupCache data is ready.");
			let idx = items.findIndex(i => i.placeholder && i.placeholder == 'groupmenu');
			if (idx != -1) {
				items[idx].visible = false;
				items.splice(idx, 0, ...GroupCache.getMenuTree());
				if (treeViewRef.value && treeViewRef.value.instance && typeof treeViewRef.value.instance.getDataSource === 'function') {
					treeViewRef.value.instance.getDataSource()?.reload();
				}
			}
			// Update selection now that the menu items are loaded
			updateSelection();

			// Now we can watch for changes to the group cache and update the menu tree by removing all existing items and replacing with new ones.
			watch(GroupCache.getCacheRef(), () => {
				console.log("SideNavMenu: GroupCache data changed.");
				// Record all groups that were expanded before we start our update.
				let expandedGroups = [];
				for (let i = 0; i < items.length; i++) {
					if (items[i]['_menuTree'] && items[i]['expanded']) {
						expandedGroups.push(items[i]['_menuTree'][0].id);
					}
				}
				
				// We need to remove all existing items with a _menuTree property, but we can't reassign items because it's used in the data source (so no filter, etc.)
				for (let i = items.length - 1; i >= 0; i--) {
					if (items[i]['_menuTree']) {
						items.splice(i, 1);
					}
				}
				let idx = items.findIndex(i => i.placeholder && i.placeholder == 'groupmenu');
				if (idx != -1) {
					items.splice(idx, 0, ...GroupCache.getMenuTree());
				}

				// Now expand all groups that were expanded before the update, and refresh the tree view.
				for (let i = 0; i < items.length; i++) {
					if (items[i]['_menuTree'] && expandedGroups.includes(items[i]['_menuTree'][0].id)) {
						items[i].expanded = true;
						console.log("Expanding group " + items[i]['_menuTree'][0].id);
					}
				}
				if (treeViewRef.value && treeViewRef.value.instance) {
					treeViewRef.value.instance.getDataSource().reload();
				}
				// Update selection after menu items are refreshed
				updateSelection();
			});
		});
		
		// Handle right click on menu item to render a context menu.
		function treeViewContextMenu(e) {
			let contextItem = e.itemData['_menuTree'] ?? null;
			let contextIsGroup = contextItem && contextItem[1] == null;
			contextItemActive.value = !contextItem ? null : (contextIsGroup ? contextItem[0] : contextItem[1]);
			contextMenuItems.value.find(i => i.id == 'edit').visible = contextItem && !contextIsGroup;
			contextMenuItems.value.find(i => i.id == 'delete').visible = contextItem;
			// Reorder option is always visible but disabled if already in reorder mode
			contextMenuItems.value.find(i => i.id == 'reorder').disabled = reorderMode.value;
		}

		// Handle click on actual context menu entry.
		function contextMenuItemClick(e) {
			if (e.itemData.id == 'edit' && contextItemActive.value) {
				router.push(`/layout/${contextItemActive.value.getSlugPath()}`);
			} else if (e.itemData.id == 'delete' && contextItemActive.value) {
				let isPage = contextItemActive.value instanceof Page;
				router.push(`/delete/${isPage ? 'page' : 'group'}/${contextItemActive.value.id}`);
			} else if (e.itemData.id == 'reorder') {
				enterReorderMode();
			}
		}

		// ========== Reorder Mode Logic ==========

		// Enter reorder mode: snapshot current state so we can cancel
		function enterReorderMode() {
			// Create a deep snapshot of all group items (_menuTree items) for potential cancel
			originalItemsSnapshot.value = items
				.filter(item => item['_menuTree'])
				.map(item => ({
					path: item.path,
					groupId: item['_menuTree'][0].id,
					pages: item.items ? item.items.filter(p => p['_menuTree']).map(p => ({
						path: p.path,
						pageId: p['_menuTree'][1]?.id
					})) : []
				}));
			reorderMode.value = true;
		}

		// Cancel reorder mode: restore original order
		function cancelReorder() {
			if (!originalItemsSnapshot.value) {
				reorderMode.value = false;
				return;
			}
			// Restore from snapshot by reloading from GroupCache
			GroupCache.fetchGroupList();
			reorderMode.value = false;
			originalItemsSnapshot.value = null;
		}

		// Save the new order to the backend
		async function saveReorder() {
			reorderBusy.value = true;
			try {
				// Build the reorder payload: array of groups with their page orders
				const reorderData = [];
				let groupWeight = 0;
				for (const item of items) {
					if (!item['_menuTree']) continue;  // Skip non-group items (Dashboard, Management)
					const group = item['_menuTree'][0];
					const groupEntry = {
						group_id: group.id,
						menuWeight: groupWeight++,
						pages: []
					};
					// Add pages with their new weights
					if (item.items) {
						let pageWeight = 0;
						for (const pageItem of item.items) {
							if (!pageItem['_menuTree'] || !pageItem['_menuTree'][1]) continue;  // Skip "(New Page)" items
							const page = pageItem['_menuTree'][1];
							groupEntry.pages.push({
								page_id: page.id,
								menuWeight: pageWeight++
							});
						}
					}
					reorderData.push(groupEntry);
				}

				await $http.post('/group/menu/reorder', { groups: reorderData });
				notify({ message: "Menu order saved", type: "success", displayTime: 2000, position: "top right" });
				
				// Refresh the group cache to reflect changes
				GroupCache.fetchGroupList();
				reorderMode.value = false;
				originalItemsSnapshot.value = null;
			} catch (err) {
				console.error("Failed to save menu order:", err);
				notify({ message: "Failed to save menu order: " + (err.response?.data?.message || err.message), type: "error", displayTime: 4000, position: "top right" });
			} finally {
				reorderBusy.value = false;
			}
		}

		// ========== Drag and Drop Handlers ==========

		// Helper to get tree node data from a DOM element index
		function getNodeDataFromIndex(index) {
			const treeView = treeViewRef.value?.instance;
			if (!treeView) return null;
			const nodeElements = treeView.element().querySelectorAll('.dx-treeview-item');
			if (index < 0 || index >= nodeElements.length) return null;
			const nodeElement = nodeElements[index];
			const path = nodeElement.closest('.dx-treeview-node')?.getAttribute('data-item-id');
			if (!path) return null;
			
			// Find the item in our items array (including nested items)
			const findItem = (itemsArray, targetPath) => {
				for (const item of itemsArray) {
					if (item.path === targetPath) return { item, parent: null };
					if (item.items) {
						for (const child of item.items) {
							if (child.path === targetPath) return { item: child, parent: item };
						}
					}
				}
				return null;
			};
			return findItem(items, path);
		}

		// Check if an item is a reorderable group (has _menuTree with group at index 0)
		function isReorderableGroup(itemData) {
			return itemData?.item?.['_menuTree'] && itemData.parent === null;
		}

		// Check if an item is a reorderable page (has _menuTree with page at index 1, not "(New Page)")
		function isReorderablePage(itemData) {
			return itemData?.item?.['_menuTree'] && 
					itemData?.item?.['_menuTree'][1] && 
					itemData.parent !== null;
		}

		function menuDragChange(e) {
			const fromData = getNodeDataFromIndex(e.fromIndex);
			const toData = getNodeDataFromIndex(e.toIndex);

			// Only allow dragging reorderable items (groups or pages with _menuTree)
			if (!fromData || (!isReorderableGroup(fromData) && !isReorderablePage(fromData))) {
				e.cancel = true;
				return;
			}

			// Don't allow dropping inside items
			if (e.dropInsideItem) {
				e.cancel = true;
				return;
			}

			if (!toData) {
				e.cancel = true;
				return;
			}

			// Groups can only be reordered among other groups
			if (isReorderableGroup(fromData)) {
				if (!isReorderableGroup(toData)) {
					e.cancel = true;
					return;
				}
			}

			// Pages can only be reordered within the same group
			if (isReorderablePage(fromData)) {
				// Target must be a page in the same group, or the "(New Page)" item in same group
				if (!toData.parent || toData.parent !== fromData.parent) {
					e.cancel = true;
					return;
				}
			}
		}

		function menuDragEnd(e) {
			if (e.fromIndex === e.toIndex) return;

			const fromData = getNodeDataFromIndex(e.fromIndex);
			if (!fromData) return;

			// Handle group reordering
			if (isReorderableGroup(fromData)) {
				const groupItems = items.filter(i => i['_menuTree']);
				const fromItem = fromData.item;
				const fromIdx = groupItems.indexOf(fromItem);
				
				const toData = getNodeDataFromIndex(e.toIndex);
				if (!toData || !isReorderableGroup(toData)) return;
				const toIdx = groupItems.indexOf(toData.item);
				
				if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

				// Find actual indices in items array
				const actualFromIdx = items.indexOf(fromItem);
				const actualToIdx = items.indexOf(toData.item);
				
				// Remove and reinsert
				items.splice(actualFromIdx, 1);
				const insertIdx = actualFromIdx < actualToIdx ? actualToIdx : actualToIdx;
				items.splice(insertIdx, 0, fromItem);
				
				// Refresh tree view
				if (treeViewRef.value?.instance) {
					treeViewRef.value.instance.getDataSource().reload();
				}
				return;
			}

			// Handle page reordering within a group
			if (isReorderablePage(fromData)) {
				const parentGroup = fromData.parent;
				const pageItems = parentGroup.items.filter(p => p['_menuTree'] && p['_menuTree'][1]);
				const fromItem = fromData.item;
				const fromIdx = pageItems.indexOf(fromItem);

				const toData = getNodeDataFromIndex(e.toIndex);
				if (!toData || toData.parent !== parentGroup) return;
				
				let toIdx;
				if (toData.item['_menuTree'] && toData.item['_menuTree'][1]) {
					toIdx = pageItems.indexOf(toData.item);
				} else {
					// Dropping at "(New Page)" position - put at end
					toIdx = pageItems.length - 1;
				}

				if (fromIdx === -1 || toIdx === -1 || fromIdx === toIdx) return;

				// Find actual indices in parent.items array
				const actualFromIdx = parentGroup.items.indexOf(fromItem);
				const actualToIdx = parentGroup.items.indexOf(toData.item);

				// Remove and reinsert
				parentGroup.items.splice(actualFromIdx, 1);
				const insertIdx = actualFromIdx < actualToIdx ? actualToIdx : actualToIdx;
				parentGroup.items.splice(insertIdx, 0, fromItem);

				// Refresh tree view
				if (treeViewRef.value?.instance) {
					treeViewRef.value.instance.getDataSource().reload();
				}
			}
		}

		return {
			treeViewRef,
			contextMenuRef,
			items,
			contextMenuItems,
			reorderMode,
			reorderBusy,
			forwardClick,
			handleItemClick,
			updateSelection,
			treeViewContextMenu,
			contextMenuItemClick,
			menuDragChange,
			menuDragEnd,
			saveReorder,
			cancelReorder
		};
	},
	components: {
		DxTreeView,
		DxContextMenu,
		DxSortable,
		DxButton
	}
};
</script>

<style lang="scss">
@import "../dx-styles.scss";
@import "../themes/generated/variables.additional.scss";

.side-navigation-menu {
	display: flex;
	flex-direction: column;
	min-height: 100%;
	height: 100%;
	width: 250px !important;

	.reorder-mode-banner {
		background: var(--dx-color-primary);
		color: #fff;
		padding: 10px;
		display: flex;
		flex-direction: column;
		gap: 8px;
		flex-shrink: 0;

		.reorder-mode-text {
			display: flex;
			align-items: center;
			gap: 8px;
			font-weight: 600;
			font-size: 14px;

			i {
				font-size: 18px;
			}
		}

		.reorder-mode-actions {
			display: flex;
			gap: 8px;

			.dx-button {
				flex: 1;
			}
		}
	}

	.menu-container {
		min-height: 100%;
		display: flex;
		flex: 1;
		overflow: hidden;

		// Make DxSortable fill container properly
		.dx-sortable {
			width: 100%;
		}

		.dx-treeview {
			width: 100%;

			// ## Text overflow handling
			.dx-treeview-item-content {
				overflow: hidden;
				text-overflow: ellipsis;
				white-space: nowrap;
				min-width: 0;  // Allow flex item to shrink below content size
			}
			// ##

			// ## Icon and item layout
			.dx-treeview-item {
				display: flex;
				flex-direction: row-reverse;  // Icon on right in DOM, displayed on left
				align-items: center;
				padding-left: 0;
				min-height: 36px;  // Consistent clickable height

				.dx-icon {
					flex-shrink: 0;
					width: $side-panel-min-width !important;
					margin: 0 !important;
					text-align: center;
					font-size: 24px;
				}
			}

			.dx-treeview-expander-icon-stub {
				display: none;
			}
			// ##

			// ## Arrow/expand toggle customization
			.dx-treeview-node {
				padding: 0 !important;
			}

			.dx-treeview-toggle-item-visibility {
				right: 10px;
				left: auto;
			}

			.dx-rtl .dx-treeview-toggle-item-visibility {
				left: 10px;
				right: auto;
			}
			// ##

			// ## Item levels customization
			.dx-treeview-node {
				// Level 1: Groups and top-level items (Dashboard, Management)
				&[aria-level="1"] {
					font-weight: bold;
					border-bottom: 1px solid $base-border-color;

					> .dx-treeview-item .dx-treeview-item-content {
						padding-right: 10px;  // Space before expand arrow
					}
				}

				// Level 2: Pages within groups, Management subitems
				&[aria-level="2"] {
					.dx-treeview-item {
						padding-left: 12px;  // Indent for hierarchy

						.dx-treeview-item-content {
							font-weight: normal;
							padding-right: 8px;
						}

						.dx-icon {
							width: calc($side-panel-min-width - 12px) !important;
							font-size: 20px;
						}
					}
				}
			}
			// ##
		}

		// ## Selected & Focused items customization
		.dx-treeview {
			.dx-treeview-node-container {
				.dx-treeview-node {
					&.dx-state-selected:not(.dx-state-focused) > .dx-treeview-item {
						background: transparent;
					}

					&.dx-state-selected > .dx-treeview-item * {
						color: $base-accent;
					}

					&:not(.dx-state-focused) > .dx-treeview-item.dx-state-hover {
						background-color: lighten($base-bg, 4);
					}
				}
			}
		}

		.dx-theme-generic .dx-treeview {
			.dx-treeview-node-container .dx-treeview-node.dx-state-selected.dx-state-focused > .dx-treeview-item * {
				color: inherit;
			}
		}
		// ##
	}

	// ## Reorder mode visual enhancements
	&:has(.reorder-mode-banner) {
		.menu-container .dx-treeview {
			// Add grab cursor to draggable items (groups and pages with _menuTree)
			.dx-treeview-node[aria-level="1"] > .dx-treeview-item,
			.dx-treeview-node[aria-level="2"] > .dx-treeview-item {
				cursor: grab;
				transition: background-color 0.15s ease;

				&:active {
					cursor: grabbing;
				}
			}

			// Non-draggable items (Dashboard at top, Management at bottom) - visual distinction
			// These don't have _menuTree so we can't target them with CSS alone,
			// but we'll rely on the drag handler to cancel the drag

			// Drag placeholder styling
			.dx-sortable-placeholder {
				background: rgba(var(--dx-color-primary-rgb, 51, 122, 183), 0.15);
				border: 2px dashed var(--dx-color-primary);
				border-radius: 4px;
			}

			// Item being dragged
			.dx-sortable-source-hidden {
				opacity: 0.4;
			}
		}
	}
	// ##
}
</style>
